// @givmo/compliance — a request someone was asked to sign, decoded (CCM-R1-SIGN-WEB, the signer's
// side; the backend's `CCM-R1-SIGN`, `-SIGN-B` and `CCM-R1-DOCS-READS`).
//
// ══ WHAT THIS FILE READS ═══════════════════════════════════════════════════════════════════════
//
// Each tolerantly off `unknown`, in `compliance-decode.ts`'s style:
//
//   • one request, `…/signing-requests/{id}` (`ComplianceSigningRequestDetailOut`): the request,
//     its live version with the three echoes an act must send back, each document at the version its
//     signers sign with the pages they are shown (the request's own rendition set: what the evidence
//     records as shown), every signature place and where it stands, the acts on this version, the
//     caller's own places with what they may do now, and the statement a signature affirms;
//   • the organization's sign-in check, `…/settings/signing`;
//   • one filing document's versions, `…/filing-documents/{id}` (a signed form's final is the
//     `signed_final` version made from the version its signers signed);
//   • one version's drawn pages, `…/versions/{id}/rendition` (a final's "as signed" pages, B5);
//   • the sender's side (CCM-R1-SIGN-WEB-B): what sending one version needs, `…/versions/{id}/
//     signing-places` (its places, each way the filing is sent with whether Givmo can sign it, the
//     way it will be sent (CCM-R1-SIGN-SEND-CHANNEL), who the rule says signs (B4) and why a send
//     would be refused now); a completed request's record lines (B6); and a short-lived link to
//     one of its files (the record, the export).
//
// The read carries no names, emails or typed names: a person is their user id, named by the host's
// roster. ⚠ AN UNREADABLE VALUE NEVER RESOLVES TOWARDS THE MORE CONFIDENT ANSWER: a place whose
// standing is unknown is not signed, a request whose state is unknown is not open, and a "can"
// flag that is absent is no.

import { extractList } from '@givmo/api-client/list';
import {
  decodeDocumentVersion,
  type DocumentVersion,
  type Rect,
} from './compliance-preparation-decode';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function optionalStr(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

function optionalInt(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) ? value : undefined;
}

function optionalNumber(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function oneOf<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : undefined;
}

function rect(value: unknown): Rect | undefined {
  if (!Array.isArray(value) || value.length !== 4) return undefined;
  const numbers = value.map(optionalNumber);
  if (numbers.some((n) => n === undefined)) return undefined;
  const [x0, y0, x1, y1] = numbers as number[];
  return x0! < x1! && y0! < y1! ? [x0!, y0!, x1!, y1!] : undefined;
}

function strings(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.length > 0)
    : [];
}

// ── What the server takes ──────────────────────────────────────────────────────────────────────

/**
 * The longest text each signing box may send, exactly the server's own limits (CCM-R1-SIGN-WEB-C,
 * cut 1006-7's N1): the backend's `TYPED_NAME_MAX_LENGTH`, `CAPACITY_TEXT_MAX_LENGTH`,
 * `MESSAGE_MAX_LENGTH` and `REASON_MAX_LENGTH` (`app/domain/charity_compliance_signing.py`). A box
 * that took more let a person meet pydantic's raw 422 ("body.typed_name: String should have at most
 * 100 characters"), shown as served. A browser counts UTF-16 units where the server counts code
 * points, so a box at the server's number is never looser than the server.
 */
export const SIGNING_TEXT_LIMITS = {
  /** The signer's name, typed as they sign. */
  typedName: 100,
  /** A title the organization states in its own words ("Something else"). */
  capacityText: 120,
  /** The note to the signers. */
  note: 1000,
  /** A signer's reason for declining. */
  reason: 500,
} as const;

// ── One request ────────────────────────────────────────────────────────────────────────────────

export type SigningRequestState = 'open' | 'completed' | 'cancelled' | 'expired' | 'superseded';
export type SlotStanding = 'unsigned' | 'signed' | 'withdrawn' | 'declined';
/**
 * The acts a person makes on this page. Signing is always the person pressing Sign: no AI signs
 * (D-CCM-31), so an AI approval the backend may still record reads as `unknown` here.
 */
export type SigningActKind = 'sign' | 'withdraw' | 'decline';
export type SigningSupportState = 'supported' | 'not_recorded' | 'unsupported';

/** Whether Givmo can sign a document for the intended channel, and the line the screen shows. */
export interface SigningSupport {
  state: SigningSupportState | 'unknown';
  /** The served factual line (D-SIGN-3; J-d), shown only where `factualLineApplies`. */
  factualLine?: string;
  factualLineApplies: boolean;
  reason?: string;
}

/** One page as the signers see it, and how the form's places land on it. */
export interface SigningPage {
  page: number;
  imageUrl: string;
  imageSha256: string;
  /** The image's own size, in pixels. */
  width: number;
  height: number;
  /** Pixels per PDF point; with the crop box, places a PDF rectangle on the image. */
  scale?: number;
  cropBox?: Rect;
  rotation: number;
}

/** One document of the package, at the version its signers sign (its D0). */
export interface SigningDocument {
  documentId: string;
  documentVersionId: string;
  contentSha256: string;
  pageCount: number;
  position: number;
  adapterKey: string;
  support: SigningSupport;
  pages: SigningPage[];
}

/** One signature place: where it is, who signs it, the capacity the organization states. */
export interface SigningSlot {
  slotId: string;
  slotKey: string;
  documentId: string;
  signerUserId: string;
  /** Stated by the organization; Givmo does not check it. */
  capacityText: string;
  page: number;
  /** Where the signature is drawn, in PDF points from the page's bottom left. */
  rect: Rect;
  position: number;
  standing: SlotStanding | 'unknown';
  standingActAt?: string;
  /** Signed, and the signer no longer holds Compliance access: the signature stands (D-SIGN-12). */
  signedThenLostAccess: boolean;
  /** Not yet signed, and the signer no longer holds access: they cannot sign it. */
  cannotSignLostAccess: boolean;
}

/** One act on the live version. */
export interface SigningAct {
  actId: string;
  actorUserId: string;
  kind: SigningActKind | 'unknown';
  occurredAt?: string;
  reason?: string;
  slotIds: string[];
}

/** The version the signers act on, with the three echoes every act sends back. */
export interface SigningVersion {
  requestVersionId: string;
  versionNumber: number;
  manifestSha256: string;
  packageDigest: string;
  renditionSetId: string;
  message?: string;
  /** The zone the request's dates are written in (the manifest's; the record's). */
  timeZone?: string;
  issuedAt?: string;
  issuedByUserId?: string;
  /** How the filing will be sent, as the request was issued (a revision keeps it). */
  intendedChannel?: string;
}

/** The caller's own places on the live version, and what they may do now. */
export interface MySlots {
  slotIds: string[];
  canSign: boolean;
  canDecline: boolean;
  canWithdraw: boolean;
}

export interface SigningRequestInfo {
  requestId: string;
  filingCaseId: string;
  state: SigningRequestState | 'unknown';
  expiresAt?: string;
  createdAt?: string;
  completedAt?: string;
  cancelledAt?: string;
  cancelledByUserId?: string;
  expiredAt?: string;
  issuedByUserId?: string;
  supersededByRequestId?: string;
  /** The version a revision is made from (`expected_version_number`). */
  liveVersionNumber?: number;
}

/**
 * One person a completed request's signing record holds sign-in details for, or held until a
 * removal took them (`people`, backend CCM-R1-SIGN-REDACT-EARLIER-SIGNERS): whom the record
 * card's removal offers, as served.
 */
export interface SigningRecordPerson {
  userId: string;
  /** A signature of theirs stands on the version the request completed on. */
  signed: boolean;
  /** They sent the request. */
  sent: boolean;
  /** The title stated for the place their latest act covers; absent for a sender who never acted. */
  capacityText?: string;
  /** The name the request kept for them as its sender. */
  sentAs?: string;
  /**
   * The record's own line for them where no signature of theirs stands (an act's heading in its
   * details, or its sent line), in the record's words and time zone: shown as served.
   */
  line?: string;
}

/** One request as one read answers it: the signer's view and the sender's together. */
export interface SigningRequestDetail {
  request: SigningRequestInfo;
  version: SigningVersion;
  documents: SigningDocument[];
  slots: SigningSlot[];
  acts: SigningAct[];
  mySlots: MySlots;
  /** The statement a signature affirms, shown directly above Sign (served; approved words). */
  intent: { statementId: string; text: string };
  caseWithdrawn: boolean;
  /**
   * The refusal a signer's act on this request meets now, in the server's own code and words
   * (`refusal`, backend CCM-R1-SIGN-WITHDRAWN-READ): served while the request's filing case is not
   * open, with every `can_*` false. The signer page shows its `detail` as served
   * (CCM-R1-SIGN-WEB-WITHDRAWN). Absent from an older backend, and wherever no words came with it.
   */
  refusal?: SigningRefusal;
  /**
   * Everyone the signing record holds sign-in details for, or held, in the served order:
   * standing signers, then everyone else who acted, then a sender who never did. Empty until
   * the request completes, and from a backend that doesn't serve it.
   */
  people: SigningRecordPerson[];
}

/** A refusal as the server words it: its code, and the line a person is shown. */
export interface SigningRefusal {
  errorCode: string;
  detail: string;
}

const REQUEST_STATES = ['open', 'completed', 'cancelled', 'expired', 'superseded'] as const;

function decodeSupport(raw: unknown): SigningSupport {
  if (!isRecord(raw)) return { state: 'unknown', factualLineApplies: false };
  return {
    state: oneOf(raw.state, ['supported', 'not_recorded', 'unsupported'] as const) ?? 'unknown',
    factualLine: optionalStr(raw.factual_line),
    factualLineApplies: raw.factual_line_applies === true,
    reason: optionalStr(raw.reason),
  };
}

function decodePage(raw: unknown): SigningPage | undefined {
  if (!isRecord(raw)) return undefined;
  const page = optionalInt(raw.page);
  const imageUrl = optionalStr(raw.image_url);
  const width = optionalInt(raw.width);
  const height = optionalInt(raw.height);
  if (page === undefined || !imageUrl || width === undefined || height === undefined) {
    return undefined;
  }
  const scale = optionalNumber(raw.scale);
  return {
    page,
    imageUrl,
    imageSha256: str(raw.image_sha256),
    width,
    height,
    ...(scale !== undefined && scale > 0 ? { scale } : {}),
    ...(rect(raw.crop_box) !== undefined ? { cropBox: rect(raw.crop_box) } : {}),
    rotation: optionalInt(raw.rotation) ?? 0,
  };
}

function decodeDocument(raw: unknown): SigningDocument | undefined {
  if (!isRecord(raw)) return undefined;
  const documentId = optionalStr(raw.document_id);
  const documentVersionId = optionalStr(raw.document_version_id);
  const contentSha256 = optionalStr(raw.content_sha256);
  if (!documentId || !documentVersionId || !contentSha256) return undefined;
  const pages = Array.isArray(raw.pages)
    ? raw.pages.flatMap((page) => {
        const decoded = decodePage(page);
        return decoded === undefined ? [] : [decoded];
      })
    : [];
  return {
    documentId,
    documentVersionId,
    contentSha256,
    pageCount: optionalInt(raw.page_count) ?? pages.length,
    position: optionalInt(raw.position) ?? 0,
    adapterKey: str(raw.adapter_key),
    support: decodeSupport(raw.support),
    pages: pages.sort((a, b) => a.page - b.page),
  };
}

function decodeSlot(raw: unknown): SigningSlot | undefined {
  if (!isRecord(raw)) return undefined;
  const slotId = optionalStr(raw.slot_id);
  const documentId = optionalStr(raw.document_id);
  const signerUserId = optionalStr(raw.signer_user_id);
  const page = optionalInt(raw.page);
  const place = rect(raw.rect);
  if (!slotId || !documentId || !signerUserId || page === undefined || place === undefined) {
    return undefined;
  }
  return {
    slotId,
    slotKey: str(raw.slot_key),
    documentId,
    signerUserId,
    capacityText: str(raw.capacity_text),
    page,
    rect: place,
    position: optionalInt(raw.position) ?? 0,
    standing:
      oneOf(raw.standing, ['unsigned', 'signed', 'withdrawn', 'declined'] as const) ?? 'unknown',
    standingActAt: optionalStr(raw.standing_act_at),
    signedThenLostAccess: raw.signed_then_lost_access === true,
    cannotSignLostAccess: raw.cannot_sign_lost_access === true,
  };
}

function decodeAct(raw: unknown): SigningAct | undefined {
  if (!isRecord(raw)) return undefined;
  const actId = optionalStr(raw.act_id);
  const actorUserId = optionalStr(raw.actor_user_id);
  if (!actId || !actorUserId) return undefined;
  return {
    actId,
    actorUserId,
    kind: oneOf(raw.kind, ['sign', 'withdraw', 'decline'] as const) ?? 'unknown',
    occurredAt: optionalStr(raw.occurred_at),
    reason: optionalStr(raw.reason),
    slotIds: strings(raw.slot_ids),
  };
}

function decodeMySlots(raw: unknown): MySlots {
  if (!isRecord(raw)) {
    return {
      slotIds: [],
      canSign: false,
      canDecline: false,
      canWithdraw: false,
    };
  }
  return {
    slotIds: strings(raw.slot_ids),
    canSign: raw.can_sign === true,
    canDecline: raw.can_decline === true,
    canWithdraw: raw.can_withdraw === true,
  };
}

function decodeRequestInfo(raw: unknown): SigningRequestInfo | undefined {
  if (!isRecord(raw)) return undefined;
  const requestId = optionalStr(raw.request_id);
  const filingCaseId = optionalStr(raw.filing_case_id);
  if (!requestId || !filingCaseId) return undefined;
  return {
    requestId,
    filingCaseId,
    state: oneOf(raw.state, REQUEST_STATES) ?? 'unknown',
    expiresAt: optionalStr(raw.expires_at),
    createdAt: optionalStr(raw.created_at),
    completedAt: optionalStr(raw.completed_at),
    cancelledAt: optionalStr(raw.cancelled_at),
    cancelledByUserId: optionalStr(raw.cancelled_by_user_id),
    expiredAt: optionalStr(raw.expired_at),
    issuedByUserId: optionalStr(raw.issued_by_user_id),
    supersededByRequestId: optionalStr(raw.superseded_by_request_id),
    liveVersionNumber: optionalInt(raw.live_version_number),
  };
}

/** A served refusal, or none: a refusal is shown only in the server's own words, so no words, none. */
function decodeRefusal(raw: unknown): SigningRefusal | undefined {
  if (!isRecord(raw)) return undefined;
  const detail = optionalStr(raw.detail);
  return detail !== undefined ? { errorCode: str(raw.error_code), detail } : undefined;
}

function decodeVersion(raw: unknown): SigningVersion | undefined {
  if (!isRecord(raw)) return undefined;
  const requestVersionId = optionalStr(raw.request_version_id);
  const manifestSha256 = optionalStr(raw.manifest_sha256);
  const packageDigest = optionalStr(raw.package_digest);
  const renditionSetId = optionalStr(raw.rendition_set_id);
  const versionNumber = optionalInt(raw.version_number);
  if (
    !requestVersionId ||
    !manifestSha256 ||
    !packageDigest ||
    !renditionSetId ||
    versionNumber === undefined
  ) {
    return undefined;
  }
  return {
    requestVersionId,
    versionNumber,
    manifestSha256,
    packageDigest,
    renditionSetId,
    message: optionalStr(raw.message),
    timeZone: optionalStr(raw.time_zone),
    issuedAt: optionalStr(raw.issued_at),
    issuedByUserId: optionalStr(raw.issued_by_user_id),
    intendedChannel: optionalStr(raw.intended_channel),
  };
}

/**
 * One request, or `undefined` when the read lacks what an act must echo (the request, its live
 * version's three echoes, the statement): a page that cannot send the exact echoes offers no act.
 */
export function decodeSigningRequestDetail(body: unknown): SigningRequestDetail | undefined {
  if (!isRecord(body)) return undefined;
  const request = decodeRequestInfo(body.request);
  const version = decodeVersion(body.version);
  const intent = isRecord(body.intent_statement) ? body.intent_statement : undefined;
  const statementId = intent !== undefined ? optionalStr(intent.statement_id) : undefined;
  const text = intent !== undefined ? optionalStr(intent.text) : undefined;
  if (request === undefined || version === undefined || !statementId || !text) return undefined;
  const list = <T>(value: unknown, decode: (raw: unknown) => T | undefined): T[] =>
    Array.isArray(value)
      ? value.flatMap((raw) => {
          const decoded = decode(raw);
          return decoded === undefined ? [] : [decoded];
        })
      : [];
  const refusal = decodeRefusal(body.refusal);
  return {
    request,
    version,
    documents: list(body.documents, decodeDocument).sort((a, b) => a.position - b.position),
    slots: list(body.slots, decodeSlot).sort((a, b) => a.position - b.position),
    acts: list(body.acts, decodeAct),
    mySlots: decodeMySlots(body.my_slots),
    intent: { statementId, text },
    caseWithdrawn: body.case_withdrawn === true,
    ...(refusal !== undefined ? { refusal } : {}),
    people: list(body.people, decodeRecordPerson),
  };
}

function decodeRecordPerson(raw: unknown): SigningRecordPerson | undefined {
  if (!isRecord(raw)) return undefined;
  const userId = optionalStr(raw.user_id);
  if (userId === undefined) return undefined;
  const capacityText = optionalStr(raw.capacity_text);
  const sentAs = optionalStr(raw.sent_as);
  const line = optionalStr(raw.line);
  return {
    userId,
    signed: raw.signed === true,
    sent: raw.sent === true,
    ...(capacityText !== undefined ? { capacityText } : {}),
    ...(sentAs !== undefined ? { sentAs } : {}),
    ...(line !== undefined ? { line } : {}),
  };
}

// ── Whom a removal reaches, organization-wide (CCM-R1-SIGN-REDACT-UNFINISHED-REQUESTS) ─────────

/** One person the organization's signing requests hold sign-in details for, completed or not. */
export interface SigningPerson {
  userId: string;
  /** The request to direct their removal on: the newest one holding their details. */
  requestId: string;
  /** How many of the organization's signing requests hold their details. */
  requests: number;
  /** The title stated for the place their latest act covers; absent when only requests they sent do. */
  capacityText?: string;
  /** The name the newest request they sent kept for them. */
  sentAs?: string;
}

/** One page of the people read, newest first; a row this build can't read is left out. */
export function decodeSigningPeople(body: unknown): SigningPerson[] {
  return extractList<unknown>(body).flatMap((raw) => {
    if (!isRecord(raw)) return [];
    const userId = optionalStr(raw.user_id);
    const requestId = optionalStr(raw.request_id);
    const requests = optionalInt(raw.requests);
    if (userId === undefined || requestId === undefined || requests === undefined) return [];
    const capacityText = optionalStr(raw.capacity_text);
    const sentAs = optionalStr(raw.sent_as);
    return [
      {
        userId,
        requestId,
        requests,
        ...(capacityText !== undefined ? { capacityText } : {}),
        ...(sentAs !== undefined ? { sentAs } : {}),
      },
    ];
  });
}

// ── The organization's sign-in check before signing (D-SIGN-1) ─────────────────────────────────

export type SigningLevel =
  | 'signed_in'
  | 'reauth_within_day'
  | 'reauth_within_30_minutes'
  | 'reauth_every_act';

export const SIGNING_LEVELS: readonly SigningLevel[] = [
  'signed_in',
  'reauth_within_day',
  'reauth_within_30_minutes',
  'reauth_every_act',
];

export interface SigningSetting {
  level: SigningLevel;
  /** 0 until the organization first changes it; a change is made FROM this revision. */
  revision: number;
  changedAt?: string;
  changedByUserId?: string;
}

/** The setting, or `undefined` for a body whose level this build does not know (never a guess). */
export function decodeSigningSetting(body: unknown): SigningSetting | undefined {
  if (!isRecord(body)) return undefined;
  const level = oneOf(body.level, SIGNING_LEVELS);
  if (level === undefined) return undefined;
  return {
    level,
    revision: optionalInt(body.revision) ?? 0,
    changedAt: optionalStr(body.changed_at),
    changedByUserId: optionalStr(body.changed_by_user_id),
  };
}

// ── A filing document's versions, and a version's drawn pages ──────────────────────────────────

/** Every version of one filing document, oldest first. */
export function decodeFilingDocumentVersions(body: unknown): DocumentVersion[] {
  if (!isRecord(body)) return [];
  return extractList<unknown>(body.versions).flatMap((raw) => {
    const decoded = decodeDocumentVersion(raw);
    return decoded === undefined ? [] : [decoded];
  });
}

/** The signed final made from the version its signers signed, if one has been composed. */
export function signedFinalOf(
  versions: readonly DocumentVersion[],
  signedVersionId: string,
): DocumentVersion | undefined {
  return versions.find(
    (version) =>
      version.kind === 'signed_final' && version.derivedFromVersionId === signedVersionId,
  );
}

/** One drawn page of a version. */
export interface RenditionPage {
  page: number;
  imageUrl: string;
  width: number;
  height: number;
}

/** A version's drawn pages, in page order; none for an unreadable body. */
export function decodeVersionRendition(body: unknown): RenditionPage[] {
  if (!isRecord(body) || !Array.isArray(body.pages)) return [];
  return body.pages
    .flatMap((raw) => {
      if (!isRecord(raw)) return [];
      const page = optionalInt(raw.page);
      const imageUrl = optionalStr(raw.image_url);
      const width = optionalInt(raw.width);
      const height = optionalInt(raw.height);
      if (page === undefined || !imageUrl || width === undefined || height === undefined) return [];
      return [{ page, imageUrl, width, height }];
    })
    .sort((a, b) => a.page - b.page);
}

// ── Sending a request (CCM-R1-SIGN-WEB-B, the issuer's side) ───────────────────────────────────

/** One way the filing is sent, and whether Givmo can sign the version for it (in the rule's order). */
export interface SigningChannel {
  channel: string;
  support: SigningSupport;
}

/** Who the case's rule records must sign (B4), verbatim: never checked against anyone's office. */
export interface SignerRequirement {
  signatureCount: number;
  roles: string[];
  notarizationRequired?: boolean;
}

/** One signature place the version's form declares. */
export interface SigningPlace {
  slotKey: string;
  page: number;
}

/** What sending one version for signature needs, before any request exists. */
export interface SigningPlaces {
  documentId: string;
  documentVersionId: string;
  filingCaseId: string;
  formNumber?: string;
  places: SigningPlace[];
  channels: SigningChannel[];
  /**
   * The way the filing will be sent, which a request records (CCM-R1-SIGN-SEND-CHANNEL): email
   * where Givmo will email it. Absent from an older backend.
   */
  sendChannel?: string;
  signerRequirement?: SignerRequirement;
  /** Why a send would be refused now, in the server's words. It never says a request is open. */
  versionRefusal?: { errorCode: string; detail: string };
}

/** The pre-issue read, or `undefined` when it lacks the version it is about. */
export function decodeSigningPlaces(body: unknown): SigningPlaces | undefined {
  if (!isRecord(body)) return undefined;
  const documentId = optionalStr(body.document_id);
  const documentVersionId = optionalStr(body.document_version_id);
  const filingCaseId = optionalStr(body.filing_case_id);
  if (!documentId || !documentVersionId || !filingCaseId) return undefined;
  const places = Array.isArray(body.places)
    ? body.places.flatMap((raw) => {
        if (!isRecord(raw)) return [];
        const slotKey = optionalStr(raw.slot_key);
        const page = optionalInt(raw.page);
        return slotKey !== undefined && page !== undefined ? [{ slotKey, page }] : [];
      })
    : [];
  const channels = Array.isArray(body.channels)
    ? body.channels.flatMap((raw) => {
        if (!isRecord(raw)) return [];
        const channel = optionalStr(raw.channel);
        return channel !== undefined ? [{ channel, support: decodeSupport(raw.support) }] : [];
      })
    : [];
  const requirement = isRecord(body.signer_requirement) ? body.signer_requirement : undefined;
  const signatureCount =
    requirement !== undefined ? optionalInt(requirement.signature_count) : undefined;
  const form = isRecord(body.form) ? body.form : undefined;
  const refusal = isRecord(body.version_refusal) ? body.version_refusal : undefined;
  const refusalCode = refusal !== undefined ? optionalStr(refusal.error_code) : undefined;
  const refusalDetail = refusal !== undefined ? optionalStr(refusal.detail) : undefined;
  const sendChannel = optionalStr(body.send_channel);
  return {
    documentId,
    documentVersionId,
    filingCaseId,
    ...(sendChannel !== undefined ? { sendChannel } : {}),
    ...(form !== undefined && optionalStr(form.form_number) !== undefined
      ? { formNumber: optionalStr(form.form_number) }
      : {}),
    places,
    channels,
    ...(requirement !== undefined && signatureCount !== undefined
      ? {
          signerRequirement: {
            signatureCount,
            roles: strings(requirement.roles),
            ...(typeof requirement.notarization_required === 'boolean'
              ? { notarizationRequired: requirement.notarization_required }
              : {}),
          },
        }
      : {}),
    ...(refusalCode !== undefined && refusalDetail !== undefined
      ? { versionRefusal: { errorCode: refusalCode, detail: refusalDetail } }
      : {}),
  };
}

/** A completed request's signing record as a page shows it beside the signed form (B6). */
export interface SigningRecordLines {
  requestId: string;
  /** The record's own first-page lines, served; the last says what the record doesn't show. */
  lines: string[];
  completedAt?: string;
  /**
   * Each document the request covered, in order, under its title with the lines about it alone
   * (CCM-R1-SIGN-RECORD-DOCS, D-CCM-101). Empty from a backend before it, as the field's own default.
   */
  documents: SigningRecordDocument[];
}

/** One document of the record: its title, as its signed file is named, and its own lines. */
export interface SigningRecordDocument {
  title: string;
  lines: string[];
}

export function decodeSigningRecordLines(body: unknown): SigningRecordLines | undefined {
  if (!isRecord(body)) return undefined;
  const requestId = optionalStr(body.request_id);
  const lines = strings(body.lines);
  if (!requestId || lines.length === 0) return undefined;
  const documents = (Array.isArray(body.documents) ? body.documents : []).flatMap(
    (row): SigningRecordDocument[] => {
      if (!isRecord(row)) return [];
      const title = optionalStr(row.title);
      const own = strings(row.lines);
      return title !== undefined && own.length > 0 ? [{ title, lines: own }] : [];
    },
  );
  return { requestId, lines, completedAt: optionalStr(body.completed_at), documents };
}

/** A short-lived link to one file a completed request left behind, and the name it downloads as. */
export interface SigningFile {
  downloadUrl: string;
  fileName: string;
}

export function decodeSigningFile(body: unknown): SigningFile | undefined {
  if (!isRecord(body)) return undefined;
  const downloadUrl = optionalStr(body.download_url);
  if (!downloadUrl) return undefined;
  return { downloadUrl, fileName: str(body.file_name) };
}
